/**
 * Fetches messages for a conversation and provides send (RULE §13: hooks for API interaction).
 * Returns messages, loading, error, sendMessage, markAsRead, refetch.
 */
import { useState, useEffect, useCallback } from 'react';
import messagingService from '../services/messagingService';

function useConversationMessages(conversationId, options = {}) {
  const { markReadOnMount = true } = options;
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async (pageNum = 1, append = false) => {
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    if (pageNum === 1) setLoading(true);
    setError(null);
    try {
      const data = await messagingService.getMessages(conversationId, {
        page: pageNum,
        page_size: 50,
      });
      const list = data?.messages ?? [];
      const totalCount = data?.total ?? list.length;
      const hasNext = data?.has_next ?? false;
      if (append) {
        setMessages((prev) => [...prev, ...list]);
      } else {
        setMessages(list);
      }
      setPage(pageNum);
      setTotal(totalCount);
      setHasMore(hasNext);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load messages');
      if (!append) setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(1, false);
      if (markReadOnMount) {
        messagingService.markConversationRead(conversationId).catch(() => {});
      }
    } else {
      setMessages([]);
      setLoading(false);
    }
  }, [conversationId, fetchMessages, markReadOnMount]);

  const sendMessage = useCallback(async (content, messageType = 'text') => {
    if (!conversationId || !content?.trim()) return null;
    setSending(true);
    try {
      const created = await messagingService.sendMessage(conversationId, {
        content: content.trim(),
        message_type: messageType,
      });
      setMessages((prev) => [...prev, created]);
      return created;
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to send message');
      return null;
    } finally {
      setSending(false);
    }
  }, [conversationId]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) fetchMessages(page + 1, true);
  }, [hasMore, loading, page, fetchMessages]);

  const updateMessage = useCallback(async (messageId, content) => {
    try {
      const res = await messagingService.editMessage(messageId, content);
      const updated = res?.data?.message_data ?? res?.message_data ?? res;
      if (updated) {
        setMessages((prev) =>
          prev.map((m) => (String(m.id) === String(messageId) ? { ...m, ...updated } : m))
        );
      }
      return updated;
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to update message');
      return null;
    }
  }, []);

  const removeMessage = useCallback(async (messageId) => {
    try {
      await messagingService.deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete message');
    }
  }, []);

  const markAsRead = useCallback(() => {
    if (conversationId) {
      messagingService.markConversationRead(conversationId).catch(() => {});
    }
  }, [conversationId]);

  return {
    messages,
    loading,
    sending,
    error,
    hasMore,
    total,
    sendMessage,
    updateMessage,
    removeMessage,
    markAsRead,
    refetch: () => fetchMessages(1, false),
    loadMore,
  };
}

export default useConversationMessages;
